import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
    DragStartEvent,
    DragEndEvent
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    useSortable,
    rectSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, IconButton, Typography, CircularProgress } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplayIcon from '@mui/icons-material/Replay';

export interface UploadStatus<T> {
    status: 'compressing' | 'uploading' | 'success' | 'error';
    image?: T;
}

interface MultiImageUploaderProps<TImage> {
    maxImages: number;
    confirmedImages: TImage[];
    setConfirmedImages: React.Dispatch<React.SetStateAction<TImage[]>>;
    uploads: Record<string, UploadStatus<TImage>>;
    onUpload: (file: File, localId: string) => void;
    getImageId: (image: TImage) => string;
    getImageUrl: (image: TImage) => string;
    onRemoveUpload?: (localId: string) => void;
}

export function MultiImageUploader<TImage>({
                                               maxImages,
                                               confirmedImages,
                                               setConfirmedImages,
                                               uploads,
                                               onUpload,
                                               getImageId,
                                               getImageUrl,
                                               onRemoveUpload,
                                           }: MultiImageUploaderProps<TImage>) {
    const [activeId, setActiveId] = useState<string | null>(null);
    const [fileMap, setFileMap] = useState<Record<string, File>>({});
    const previewUrlsRef = useRef<Record<string, string>>({});
    const hasMountedRef = useRef(false);

    const activeImage = useMemo(() => {
        return confirmedImages.find((img) => getImageId(img) === activeId) || null;
    }, [activeId, confirmedImages, getImageId]);

    const onDrop = useCallback((acceptedFiles: File[]) => {
        const slotsLeft = maxImages - confirmedImages.length;
        const filesToUpload = acceptedFiles.slice(0, slotsLeft);

        filesToUpload.forEach((file) => {
            const localId = crypto.randomUUID();
            setFileMap((prev) => ({ ...prev, [localId]: file }));

            onUpload(file, localId);
        });
    }, [maxImages, confirmedImages.length, onUpload]);

    const {
        getRootProps,
        getInputProps,
        isDragActive,
    } = useDropzone({
        onDrop,
        accept: {
            'image/jpeg': [],
            'image/png': [],
            'image/webp': [],
        },
        multiple: true,
        noClick: confirmedImages.length >= maxImages,
        noDrag: confirmedImages.length >= maxImages,
    });

    const sensors = useSensors(useSensor(PointerSensor));

    useEffect(() => {
        if (!hasMountedRef.current) {
            hasMountedRef.current = true;
            return; // Skip on first mount
        }

        const uploadsArray = Object.entries(uploads);
        const successfulImages = uploadsArray
            .filter(([_, upload]) => upload.status === 'success' && upload.image)
            .map(([localId, upload]) => ({ localId, image: upload.image! }));

        if (successfulImages.length > 0) {
            const localIdsToRemove = successfulImages.map(({ localId }) => localId);

            setConfirmedImages((prev) => {
                const existingIds = new Set(prev.map(getImageId));
                const newImages = successfulImages
                    .map(({ image }) => image)
                    .filter((image) => !existingIds.has(getImageId(image)));
                return [...prev, ...newImages];
            });

            for (const localId of localIdsToRemove) {
                onRemoveUpload?.(localId);
            }
        }
    }, [uploads, getImageId, setConfirmedImages, onRemoveUpload]);

    const handleDragStart = (event: DragStartEvent) => {
        setActiveId(event.active.id as string);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        setActiveId(null);

        if (!over || active.id === over.id) return;

        const oldIndex = confirmedImages.findIndex(img => getImageId(img) === active.id);
        const newIndex = confirmedImages.findIndex(img => getImageId(img) === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
            setConfirmedImages(arrayMove(confirmedImages, oldIndex, newIndex));
        }
    };

    const retryUpload = (localId: string) => {
        const file = fileMap[localId];
        if (!file) return;
        onUpload(file, localId);
    };

    useEffect(() => {
        const urlsToCleanUp = { ...previewUrlsRef.current };
        return () => {
            for (const url of Object.values(urlsToCleanUp)) {
                URL.revokeObjectURL(url);
            }
        };
    }, []);

    return (
        <Box>
            <Box
                {...getRootProps()}
                sx={{
                    border: '1px dashed gray',
                    borderRadius: '5px',
                    padding: 2,
                    textAlign: 'center',
                    backgroundColor: isDragActive ? '#f0f0f0' : '#fafafa',
                    cursor: confirmedImages.length >= maxImages ? 'not-allowed' : 'pointer',
                }}
            >
                <input {...getInputProps()} />
                <Typography variant="body2">
                    {confirmedImages.length >= maxImages
                        ? `Maximal ${maxImages} Bilder`
                        : 'Ziehe Bilder hierher oder klicke zum Hinzufügen'}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                    Du kannst die Reihenfolge der Bilder per Drag & Drop ändern.
                </Typography>
            </Box>

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={confirmedImages.map(getImageId)}
                    strategy={rectSortingStrategy}
                >
                    {confirmedImages.length > 0 && (
                        <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
                            {confirmedImages.map((img) => (
                                <SortableConfirmedImage
                                    key={getImageId(img)}
                                    id={getImageId(img)}
                                    url={getImageUrl(img)}
                                    onRemove={() =>
                                        setConfirmedImages((prev) =>
                                            prev.filter((i) => getImageId(i) !== getImageId(img))
                                        )
                                    }
                                />
                            ))}
                        </Box>
                    )}
                </SortableContext>

                <DragOverlay>
                    {activeImage && (
                        <Box sx={{
                            width: 100,
                            height: 100,
                            borderRadius: 2,
                            overflow: 'hidden',
                            boxShadow: 3,
                            cursor: 'move',
                        }}>
                            <img
                                src={getImageUrl(activeImage)}
                                alt="Dragging"
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                        </Box>
                    )}
                </DragOverlay>
            </DndContext>

            {Object.entries(uploads).length > 0 && (
                <Box mt={2} display="flex" gap={1} flexWrap="wrap">
                    {Object.entries(uploads).map(([id, upload]) => {
                        const file = fileMap[id];
                        if (!file) return null;

                        if (!previewUrlsRef.current[id]) {
                            previewUrlsRef.current[id] = URL.createObjectURL(file);
                        }

                        const previewUrl = previewUrlsRef.current[id];

                        return (
                            <Box key={id} sx={{
                                width: 100,
                                height: 100,
                                borderRadius: 2,
                                overflow: 'hidden',
                                boxShadow: 1,
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: upload.status === 'error' ? '#ffeaea' : '#f5f5f5',
                                border: upload.status === 'error' ? '1px solid #ff5c5c' : undefined,
                            }}>
                                <img
                                    src={previewUrl}
                                    alt="Preview"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        opacity: upload.status === 'error' ? 0.3 : 0.5,
                                    }}
                                />
                                <Typography
                                    variant="caption"
                                    sx={{
                                        position: 'absolute',
                                        bottom: 2,
                                        left: 0,
                                        right: 0,
                                        textAlign: 'center',
                                        backgroundColor: 'rgba(255,255,255,0.7)',
                                    }}
                                    color={upload.status === 'error' ? 'error' : undefined}
                                >
                                    {upload.status === 'compressing'
                                        ? 'Komprimieren'
                                        : upload.status === 'uploading'
                                            ? 'Hochladen'
                                            : upload.status === 'error'
                                                ? 'Fehler'
                                                : ''}
                                </Typography>

                                    {upload.status === 'compressing' || upload.status === 'uploading' ? (
                                        <CircularProgress size={24} sx={{ position: 'absolute' }} />
                                    ) : null}

                                    {upload.status === 'error' && (
                                        <>
                                            <IconButton
                                                onClick={() => retryUpload(id)}
                                                size="small"
                                                sx={{
                                                    position: 'absolute',
                                                    top: 2,
                                                    left: 2,
                                                    backgroundColor: 'rgba(255,255,255,0.7)',
                                                    '&:hover': { backgroundColor: 'white' },
                                                }}
                                            >
                                                <ReplayIcon fontSize="small" />
                                            </IconButton>

                                            <IconButton
                                                onClick={() => onRemoveUpload?.(id)}
                                                size="small"
                                                sx={{
                                                    position: 'absolute',
                                                    top: 2,
                                                    right: 2,
                                                    backgroundColor: 'rgba(255,255,255,0.7)',
                                                    '&:hover': { backgroundColor: 'white' },
                                                }}
                                            >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
}

type SortableConfirmedImageProps = {
    id: string;
    url: string;
    onRemove: () => void;
};

const SortableConfirmedImage = React.memo(function SortableConfirmedImage({
                                                                              id,
                                                                              url,
                                                                              onRemove,
                                                                          }: SortableConfirmedImageProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    return (
        <Box
            ref={setNodeRef}
            sx={{
                transform: CSS.Transform.toString(transform),
                transition,
                position: 'relative',
                width: 100,
                height: 100,
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: 1,
                flexShrink: 0,
                opacity: isDragging ? 0 : 1,
            }}
            {...attributes}
        >
            <Box {...listeners} sx={{ width: '100%', height: '100%', cursor: 'move' }}>
                <img
                    src={url}
                    alt="Uploaded"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
            </Box>
            <IconButton
                onClick={(e) => {
                    e.stopPropagation();
                    onRemove();
                }}
                size="small"
                sx={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    backgroundColor: 'rgba(255,255,255,0.7)',
                    '&:hover': { backgroundColor: 'white' },
                }}
            >
                <DeleteIcon fontSize="small" />
            </IconButton>
        </Box>
    );
});
